import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { PUBLIC_USER_ROUTE } from "../../services/graphql/queriesMutations";
import type { GistType, UserType } from "../../services/types";
import { absoluteBrowserUrl, applyPublicSeoMeta, copyToClipboard } from "./publicPageUtils";
import styles from "./PublicPages.module.css";

type PublicUserRouteData = {
  publicUserRoute: {
    canonicalUrl: string;
    shouldRedirect: boolean;
    user: UserType;
  };
};

const PublicUserPage: React.FC = () => {
  const params = useParams();
  const location = useLocation();
  const rawHandle = params.handle || params.handleWithAt || "";
  const handle = rawHandle.startsWith("@") ? rawHandle.slice(1) : rawHandle;
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<PublicUserRouteData>(PUBLIC_USER_ROUTE, {
    variables: { handle },
    skip: !handle || !location.pathname.startsWith("/@"),
  });

  const route = data?.publicUserRoute;
  const user = route?.user;
  const [copyStatus, setCopyStatus] = useState<string>("");

  useEffect(() => {
    if (route?.shouldRedirect && route.canonicalUrl) {
      navigate(route.canonicalUrl, { replace: true });
    }
  }, [navigate, route]);

  useEffect(() => {
    if (!user || !route?.canonicalUrl) return;
    applyPublicSeoMeta({
      title: `${user.displayName || user.name} (@${user.handle})`,
      description: user.bio || `Public Gistoscope profile for ${user.displayName || user.name}.`,
      canonicalPath: route.canonicalUrl,
      // Macro Patch 3 intentionally keeps profiles noindex until a dedicated
      // profile visibility setting is introduced.
      indexable: false,
    });
  }, [route, user]);

  const handleCopy = async () => {
    if (!route?.canonicalUrl) return;
    const ok = await copyToClipboard(absoluteBrowserUrl(route.canonicalUrl));
    setCopyStatus(ok ? "Copied" : "Copy failed");
    window.setTimeout(() => setCopyStatus(""), 1600);
  };

  if (loading) {
    return <div className={styles.page}><main className={styles.shell}>Loading public profile…</main></div>;
  }

  if (error || !user) {
    return <div className={styles.page}><main className={styles.shell}><div className={styles.errorBox}>Public profile not found.</div></main></div>;
  }

  const publicGists = (user.gists || []).filter((gist: GistType) => gist.status === "PUBLISHED" && gist.visibility === "PUBLIC");

  return (
    <div className={styles.page}>
      <main className={styles.shell}>
        <section className={styles.hero}>
          <p className={styles.eyebrow}>Public profile</p>
          <h1 className={styles.title}>{user.displayName || user.name}</h1>
          {user.bio ? <p className={styles.subtitle}>{user.bio}</p> : null}
          <div className={styles.metaRow}>
            <span className={styles.pill}>@{user.handle}</span>
            <span className={styles.pill}>{user.publicId}</span>
            <button type="button" className={styles.copyButtonInline} onClick={handleCopy}>Copy public link</button>
            {copyStatus ? <span className={styles.copyStatusInline}>{copyStatus}</span> : null}
          </div>
        </section>

        <section className={styles.card} style={{ marginTop: 22 }}>
          <h2 className={styles.sectionTitle}>Public gists</h2>
          <div className={styles.gistList}>
            {publicGists.length ? publicGists.map((gist: GistType) => (
              <div key={gist.id} className={styles.gistLink}>
                <h3 className={styles.gistTitle}>{gist.title}</h3>
                <p className={styles.smallText}>Subject ID: {gist.subjectId}</p>
              </div>
            )) : <p className={styles.empty}>No public gists yet.</p>}
          </div>
          <p style={{ marginTop: 18 }}><Link className={styles.inlineLink} to="/">Back to home</Link></p>
        </section>
      </main>
    </div>
  );
};

export default PublicUserPage;
